/**
 * WonSpirit Quicklink — 페이지 이동 체감 속도 극대화
 *
 * ⛔ 현재 어떤 페이지도 이 파일을 불러오지 않는다 (2026-11-02, 인프라 점검 L-1).
 *   아래 "효과 없음 확인" 대로 재사용 0 인데, 같은 세션의 동시 요청을 만들어 DB 세션 행 잠금 대기를 늘렸다.
 *   파일·UNSAFE_PATHS 는 *.won 응답을 캐시 가능하게 바꾼 뒤 다시 넣을 때를 위해 남긴다.
 *   재도입은 QuicklinkSafetyTest#noTemplateLoadsQuicklinkWhileHtmlIsNoStore 를 의도적으로 고쳐야 한다.
 *
 * prefetch 전략:
 *   1) Hover/Focus prefetch: 사용자가 링크에 손가락/포커스 올리는 순간 다음 HTML fetch 시작
 *   2) (제거됨 2026-10-24) Viewport Observer — 아래 "효과 없음 확인" 참고
 *   3) 하단 네비 우선: 페이지 로드 직후 아래 셀렉터의 링크를 prefetch
 *      ⚠ 실제 모바일 하단 네비는 nav.ms-bottom-nav 라 어느 셀렉터에도 걸리지 않는다(2026-10-24 확인 —
 *        nav.ms-nav · .ms-main-menu 는 템플릿에 없음). 지금 걸리는 곳은 nav[aria-label] 인
 *        관리자 페이징 nav(errorLog·mailTemplateList·msgSendLog) 정도다.
 *
 * ⚠ 효과 없음 확인 (2026-10-24 실측):
 *   모든 *.won 응답은 HtmlNoCacheFilter 가 Cache-Control: no-store 를 붙인다(ERR-DEPLOY-006, bfcache 옛 번들 사고).
 *   no-store 응답은 prefetch 해도 브라우저가 보관하지 않아, 사용자가 클릭하면 서버에 다시 요청한다.
 *   Chrome 155 대조 실험 — no-store/헤더 없음: 클릭 시 재요청 · max-age=300: 재사용.
 *   즉 지금 구조에서 prefetch 는 속도 이득이 0이고, 요청한 페이지마다 컨트롤러·DB 조회만 한 번씩 더 생긴다.
 *
 * 2번(Viewport) 제거 이유 (2026-10-22 운영):
 *   거래 목록처럼 행마다 링크가 있는 화면을 열면 idle 한 번에 링크 30여 개를 한꺼번에 prefetch 했다.
 *   한 세션에서 1초에 30여 건이 도착해 Hikari 풀(최대 25)이 활성 21·대기 10 까지 찼다.
 *   효과가 0인 요청이므로 개수 제한 큐를 만드는 대신 전략 자체를 뺐다.
 *   다시 넣으려면 먼저 *.won 응답이 캐시 가능해야 한다 — no-store 를 유지하는 한 되살리지 말 것.
 *   (1·3번은 요청 수가 적어 풀 포화 원인은 아니지만 같은 이유로 효과는 없다. 유지 여부는 별도 판단 —
 *    3번은 위 ⚠ 처럼 하단 네비가 아니라 관리자 페이지 번호를 로드 직후 한꺼번에 받는다)
 *
 * 안전장치:
 *   - 같은 origin 만 prefetch (CSRF·외부 요청 방지)
 *   - Save-Data 헤더 감지 시 비활성화 (데이터 절약 모드 존중)
 *   - 이미 prefetch 한 URL 은 Set 으로 중복 방지
 *   - 2G/slow-2G 연결은 스킵 (대역폭 낭비 방지)
 */
(function () {
    'use strict';

    // Save-Data 또는 느린 연결이면 prefetch 비활성 (사용자 데이터 보호)
    if (navigator.connection) {
        if (navigator.connection.saveData) return;
        if (/2g/i.test(navigator.connection.effectiveType || '')) return;
    }

    const prefetched = new Set();

    /**
     * 상태 변경 가능 GET 엔드포인트 — prefetch 절대 금지
     *
     * 이유: <link rel="prefetch"> 는 브라우저가 실제 HTTP GET 을 발송하며
     *       same-origin 이라 JSESSIONID 쿠키가 함께 전송된다.
     *       서버는 이를 정상 요청으로 처리하므로 세션을 소멸시키는
     *       /logout.won 같은 URL 은 prefetch 만으로도 사용자 세션이 끊긴다.
     *
     * 재현 사건 (2026-04-19): quicklink 도입 직후 전역 세션 풀림 장애.
     *   - 에러: layout.html:14 ${session.loginUser} → IllegalStateException
     *   - 근본 원인: hover/viewport prefetch 가 /logout.won 을 GET 호출 → session.invalidate()
     *
     * 재현 사건 #2 (ERR-FRONTEND-032): 관리자 대리 로그인 자동 복귀.
     *   - 증상: 관리자가 회원 B로 대리 로그인 후 다른 메뉴 클릭만 해도 관리자로 복귀
     *   - 근본 원인: header 의 "복귀" 버튼(/adminLoginRevert.won)이 viewport 내 상시 노출
     *       → IntersectionObserver 가 idle prefetch → 서버가 세션 loginUser 를 관리자로 복원
     *
     * 재현 사건 #3 (2026-09-02): 게시판 조회수가 글 전부 똑같이 증가.
     *   - 증상: 목록을 한 번 열 때마다 보이는 글 전부의 조회수가 동시에 +1 되어,
     *           "1시간 1회" 중복 방지 로직 때문에 모든 글이 같은 숫자로 수렴
     *   - 근본 원인: 목록의 글 제목 링크(/boardView.won)가 전부 viewport 안에 있어
     *       → idle prefetch 가 글 개수만큼 GET 발사 → 서버가 조회로 처리해 VIEW_COUNT 증가
     *   - 확인 증거: 목록 진입 1초 뒤 referer=boardList.won 인 boardView.won 요청이 글 수만큼 동시 도착
     *
     * 새 URL 추가 시: 세션/DB/쿠키를 변경하는 GET 엔드포인트는 반드시 여기에 추가하고,
     *                 더 근본적으로는 서버에서 POST 전용으로 전환한다.
     */
    const UNSAFE_PATHS = [
        /^\/logout\.won$/i,
        /^\/loginPending\.won$/i,
        /^\/loading\/refreshStats\.won/i, // force=true 면 스냅샷 강제 재계산 (무거운 사이드이펙트)
        /^\/adminLoginRevert\.won$/i,     // 관리자 대리 로그인 복귀 — 세션 loginUser 교체 (ERR-FRONTEND-032)
        /^\/adminUserLogin\.won$/i,       // 관리자 대리 로그인 시작 — 세션 adminUser 백업 + loginUser 교체
        /^\/legal\/consentRefresh\.won$/i,// 약관 재동의 POST — prefetch 로 오발송 시 세션 플래그가 꺼질 수 있음
        /^\/boardAttachDownload\.won/i,   // 게시판 첨부 다운로드 — 서버 IO/카운트 사이드이펙트, 실파일 없는 ID 면 404 콘솔 노이즈
        /^\/boardView\.won$/i,            // 게시글 상세 — 조회수 +1 · 조회 로그 INSERT (2026-09-02 전 글 조회수 동반 증가)
        /^\/login\.won$/i,                // 로그인 처리 — POST 폼인데 prefetch 가 GET 호출 시 RememberMe 자동 복원 + 세션 회전 race (ERR-SECURITY-005)
        // 상태 변경 POST 엔드포인트 계열 일괄 차단 — 이름이 Insert/Update/Delete/Save 로 끝나는 URL 은
        // 폼 제출 전용이라 prefetch(GET) 가 도착하면 파라미터 0개 요청으로 500 을 유발한다.
        // 조회용 화면은 …InsertView.won / …Write.won 처럼 별도 접미사를 쓰므로 매칭되지 않는다.
        // (prefetch 차단은 속도 이득만 포기할 뿐 기능에는 영향이 없다 — 안전한 기본값)
        /(Insert|Update|Delete|Save)\.won$/i,
    ];

    function prefetchUrl(url) {
        if (!url || prefetched.has(url)) return;
        // 같은 origin 만
        try {
            const u = new URL(url, location.href);
            if (u.origin !== location.origin) return;
            // hash 만 다른 링크, javascript:, mailto: 등 제외
            //
            // ⚠ href 문자열 비교는 hash 를 걸러내지 못한다 (2026-07-29 운영 사고).
            //   헤더 드롭다운 토글(<a href="#">)의 a.href 는 "현재URL#" 로 해석되는데,
            //   URL 직렬화가 빈 fragment 의 '#' 을 남기므로 location.href 와 문자열이 달라져
            //   과거 가드를 통과했다 → 브라우저가 fragment 를 떼고 현재 경로로 실제 GET 을 발사.
            //   /stockTransactionUpdate.won 처럼 저장 실패 시 forward 로 머무는 POST 전용 URL 에서
            //   이 GET 이 파라미터 0개로 도착해 500 + 텔레그램 HIGH 알림이 반복됐다(7명 / 16건).
            //   fragment 를 제외한 경로+쿼리로 비교해 "같은 문서" 요청을 전부 차단한다.
            const sameDocument = u.pathname === location.pathname && u.search === location.search;
            if (sameDocument) return;
            if (u.protocol !== 'http:' && u.protocol !== 'https:') return;
            // .won 확장자(HTML) + 쿼리/해시 있는 것만. 정적 자산(.js/.css/.png 등)은 제외
            const path = u.pathname;
            if (/\.(js|css|png|jpg|jpeg|gif|svg|webp|woff2?|ico|mp4|pdf)$/i.test(path)) return;
            // 상태 변경 가능 GET 엔드포인트 차단 (세션 소멸, 배치 재계산 등)
            if (UNSAFE_PATHS.some((re) => re.test(path))) return;
        } catch (e) {
            return;
        }
        prefetched.add(url);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'document';
        link.crossOrigin = 'same-origin';
        document.head.appendChild(link);
    }

    /**
     * 링크에서 prefetch 대상 URL 을 추출한다.
     *
     * 제외 조건:
     *   - download 속성: 파일 다운로드 링크
     *   - data-no-prefetch 속성: 템플릿에서 명시적으로 prefetch 차단한 링크
     *   - rel="nofollow": 외부/위험 링크 표식
     */
    function getHref(a) {
        if (!a || !a.href) return null;
        if (a.hasAttribute('download')) return null;
        if (a.hasAttribute('data-no-prefetch')) return null;
        const rel = a.getAttribute('rel');
        if (rel && /\bnofollow\b/i.test(rel)) return null;
        return a.href;
    }

    // ─── 1) Hover/Focus/Touch prefetch (가장 적극적) ───
    function onInteraction(e) {
        const a = e.target.closest && e.target.closest('a[href]');
        const href = getHref(a);
        if (href) prefetchUrl(href);
    }
    // mouseenter 보다 mouseover 가 버블링 되어 성능 좋음
    document.addEventListener('mouseover', onInteraction, { passive: true, capture: true });
    document.addEventListener('focusin', onInteraction, { passive: true, capture: true });
    // 모바일 터치 시작 (실제 클릭 전)
    document.addEventListener('touchstart', onInteraction, { passive: true, capture: true });

    // ─── 2) 뷰포트 내 링크 자동 prefetch — 제거됨 (파일 상단 "2번 제거 이유" 참고) ───

    // ─── 3) 모바일 bottom nav 즉시 prefetch ───
    function prefetchBottomNav() {
        // .ms-main-menu, .ms-nav, nav[role=tablist] 등 알려진 패턴
        const navSelectors = [
            '.ms-main-menu a[href]',
            'nav.ms-nav a[href]',
            'footer a[href]',
            'nav[aria-label] a[href]',
        ];
        navSelectors.forEach((sel) => {
            document.querySelectorAll(sel).forEach((a) => {
                const href = getHref(a);
                if (href) {
                    const schedule = window.requestIdleCallback || ((fn) => setTimeout(fn, 500));
                    schedule(() => prefetchUrl(href));
                }
            });
        });
    }

    function init() {
        prefetchBottomNav();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
